import * as React from 'react';

import { useQuery } from '@apollo/client';

import ProductCoursesQuery from 'bundles/enterprise-legacy-learner-home/queries/ProductCoursesQuery.graphql';
import type {
  ProductCoursesQuery as ProductCoursesQueryType,
  ProductCoursesQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductCoursesQuery';
import { AutoenrolledCoursesQuery } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeGraphqlQueries';
import type {
  AutoenrolledCoursesQuery as AutoenrolledCoursesQueryData,
  AutoenrolledCoursesQueryVariables,
  AutoenrolledCoursesQuery_ProgramEnrollmentSettingsV1Resource_joinProgramCourses_elements_enrollmentTarget_targetProduct_ProgramEnrollmentSettingsV1_courseEnrollmentMember as CourseEnrollment,
} from 'bundles/enterprise-legacy-learner-home/utils/__generated__/AutoenrolledCoursesQuery';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import EnterpriseAutoEnrolledCoursesContext from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type Props = {
  children: React.ReactNode;
};

/**
 * @description This provider is used to fetch the courses that are auto-enrolled when a user joins a program.
 */
const EnterpriseAutoEnrolledCoursesDataProvider = ({ children }: Props) => {
  const program = useProgram();
  const programMembership = useProgramMembership();
  const features = useEnterpriseProductFeatures();
  const { data: autoEnrolledCourseIdsResponse, loading: isAutoEnrollCourseraLoading } = useQuery<
    AutoenrolledCoursesQueryData,
    AutoenrolledCoursesQueryVariables
  >(AutoenrolledCoursesQuery, {
    skip: !program || !features?.allowOrgForCurriculumBuilder,
    variables: {
      programId: program?.id || '',
      includeEnrolledCoursesOnly: Boolean(programMembership?.isProgramMember),
    },
    ssr: false,
  });
  const allTargets = filterExistsOrDefault(
    autoEnrolledCourseIdsResponse?.ProgramEnrollmentSettingsV1Resource?.joinProgramCourses?.elements
  );
  const autoEnrolledCourseIds = allTargets
    .filter((target) => Boolean(target.triggerEventJoinedProgram))
    .map((target) => (target?.enrollmentTarget.targetProduct as CourseEnrollment).courseEnrollment.targetCourseId);
  const { data: autoEnrolledCoursesResponse, loading: isProductCoursesLoading } = useQuery<
    ProductCoursesQueryType,
    ProductCoursesQueryVariables
  >(ProductCoursesQuery, {
    skip: !autoEnrolledCourseIds?.length,
    variables: {
      courseIds: autoEnrolledCourseIds,
    },
    context: {
      clientName: 'gatewayGql',
    },
  });
  const autoEnrolledCourses = filterExistsOrDefault(autoEnrolledCoursesResponse?.Course?.queryByIds);

  return (
    <EnterpriseAutoEnrolledCoursesContext.Provider
      value={{ courses: autoEnrolledCourses, isLoading: isProductCoursesLoading || isAutoEnrollCourseraLoading }}
    >
      {children}
    </EnterpriseAutoEnrolledCoursesContext.Provider>
  );
};

export default EnterpriseAutoEnrolledCoursesDataProvider;
